import { PART_ORIGINS, type PartOrigin } from "@bt/domain";
import { type InstallationReport, share } from "@bt/domain/report";
import { useTranslation } from "react-i18next";
import type { Column, Row } from "../charts/frame";
import { useAdminFormat } from "../format";
import { Grid, ReportView, Section, SortableTable, Stats, TallyFigure } from "./kit";

type Supplier = NonNullable<NonNullable<InstallationReport["suppliers"]>["shops"]>[number];

/** Where garages buy, and how their parts are described (docs/admin-app/figures.md › The report). */
export function SourcingPage() {
  const { t } = useTranslation();
  return (
    <div className="space-y-8">
      <Section title={t("adminApp.sourcing.shops")} description={t("adminApp.sourcing.shopsHint")}>
        <ReportView>
          {(report) => <Suppliers rows={report.suppliers?.shops} kind="shops" />}
        </ReportView>
      </Section>
      <Section
        title={t("adminApp.sourcing.dealers")}
        description={t("adminApp.sourcing.dealersHint")}
      >
        <ReportView>
          {(report) => <Suppliers rows={report.suppliers?.localDealers} kind="dealers" />}
        </ReportView>
      </Section>
      <Section title={t("adminApp.sourcing.parts")} description={t("adminApp.sourcing.partsHint")}>
        <ReportView>{(report) => <Parts report={report} />}</ReportView>
      </Section>
    </div>
  );
}

function Suppliers({
  rows,
  kind,
}: {
  rows: readonly Supplier[] | undefined;
  kind: "shops" | "dealers";
}) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const list = rows ?? [];
  // Spend is per currency, never converted: the first currency each one has, with the others noted.
  const currencies = [
    ...new Set(list.flatMap((s) => (s.spend ?? []).map((m) => m.currency))),
  ].sort();
  const columns: Column[] = [
    { key: "name", label: t("adminApp.sourcing.name") },
    { key: "garages", label: t("adminApp.sourcing.garages"), numeric: true },
    { key: "sources", label: t("adminApp.sourcing.sources"), numeric: true },
    { key: "preferred", label: t("adminApp.sourcing.preferred"), numeric: true },
    { key: "orders", label: t("adminApp.sourcing.orders"), numeric: true },
    ...currencies.map((currency) => ({
      key: `spend_${currency}`,
      label: t("adminApp.sourcing.spend", { currency }),
      numeric: true,
      format: (v: unknown) => (typeof v === "number" ? format.money(v, currency) : "–"),
    })),
    {
      key: "priceAge",
      label: t("adminApp.sourcing.priceAge"),
      numeric: true,
      format: (v) =>
        typeof v === "number" ? t("adminApp.sourcing.days", { count: Math.round(v) }) : "–",
    },
  ];
  const tableRows: Row[] = list.map((s) => ({
    name: s.name ?? s.key,
    garages: s.garages ?? 0,
    sources: s.sources ?? 0,
    preferred: s.preferredSources ?? 0,
    orders: s.orders ?? 0,
    ...Object.fromEntries(
      currencies.map((currency) => [
        `spend_${currency}`,
        s.spend?.find((m) => m.currency === currency)?.amountMinor ?? null,
      ]),
    ),
    priceAge: s.priceAgeDays && s.priceAgeDays.n > 0 ? s.priceAgeDays.median : null,
  }));
  return (
    <SortableTable
      title={t(`adminApp.sourcing.${kind}Table`, { count: list.length })}
      description={t("adminApp.sourcing.tableHint")}
      columns={columns}
      rows={tableRows}
      csvName={kind}
      initialSort="garages"
    />
  );
}

function Parts({ report }: { report: InstallationReport }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const parts = report.parts;
  const coverage = parts?.catalogueCoverage;
  const preferred = report.suppliers?.preferredSource;
  if (!parts)
    return <p className="text-sm text-muted-foreground">{t("adminApp.usage.notReported")}</p>;
  return (
    <div className="space-y-4">
      <Stats
        items={[
          {
            key: "parts",
            label: t("adminApp.sourcing.partsTotal"),
            value: format.count(parts.total),
          },
          {
            key: "noSource",
            label: t("adminApp.sourcing.noSource"),
            value: format.share(share(parts.withoutSource ?? 0, parts.total ?? 0)),
            detail: t("adminApp.sourcing.noSourceHint", { count: parts.withoutSource ?? 0 }),
          },
          {
            key: "preferred",
            label: t("adminApp.sourcing.withPreferred"),
            value: format.share(share(preferred?.withPreferred ?? 0, preferred?.parts ?? 0)),
          },
          {
            key: "catalogue",
            label: t("adminApp.sourcing.catalogueCoverage"),
            value: format.share(share(coverage?.inCatalogue ?? 0, coverage?.parts ?? 0)),
            detail: t("adminApp.sourcing.catalogueCoverageHint"),
          },
        ]}
      />
      <Grid>
        <TallyFigure
          title={t("adminApp.sourcing.origin")}
          rows={parts.byOrigin}
          csvName="part-origin"
          labelOf={(v) =>
            (PART_ORIGINS as readonly string[]).includes(v)
              ? t(`partOrigin.${v as PartOrigin}`)
              : t("adminApp.sourcing.noOrigin")
          }
          base={parts.total}
        />
        <TallyFigure
          title={t("adminApp.sourcing.manufacturers")}
          description={t("adminApp.sourcing.manufacturersHint")}
          rows={parts.manufacturers}
          csvName="manufacturers"
          limit={15}
        />
      </Grid>
    </div>
  );
}
